import React, { useState, useEffect } from 'react';
import { ArrowRight, Target, Calendar, Users, Rocket, TrendingUp, Award, Linkedin, Mail, Brain, Code, BarChart, Zap, Sparkles, Sun, Moon, MapPin, BookOpen, Video, FileText, Globe, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useTheme, useLanguage } from '../context/AppContext';
import { Link } from 'react-router-dom';
import { translations } from '../translations';

const AIFactory = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const isDark = theme === 'dark';
  const t = translations[language];

  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const content = t.aiFactoryPage;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0B0F] text-white' : 'bg-white text-slate-900'} relative overflow-hidden transition-colors duration-500`}>
      {/* Advanced animated background - same as homepage */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute inset-0 ${isDark ? 'opacity-40' : 'opacity-20'}`}>
          <div 
            className="absolute w-[800px] h-[800px] rounded-full blur-3xl animate-mesh-1"
            style={{
              background: `radial-gradient(circle, ${isDark ? 'rgba(57,173,227,0.3)' : 'rgba(57,173,227,0.15)'} 0%, transparent 70%)`,
              top: '10%',
              left: '10%'
            }}
          />
          <div 
            className="absolute w-[600px] h-[600px] rounded-full blur-3xl animate-mesh-2"
            style={{
              background: `radial-gradient(circle, ${isDark ? 'rgba(0,255,209,0.25)' : 'rgba(0,255,209,0.12)'} 0%, transparent 70%)`,
              bottom: '20%',
              right: '15%'
            }}
          />
          <div 
            className="absolute w-[700px] h-[700px] rounded-full blur-3xl animate-mesh-3"
            style={{
              background: `radial-gradient(circle, ${isDark ? 'rgba(7,116,182,0.2)' : 'rgba(7,116,182,0.1)'} 0%, transparent 70%)`,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          />
        </div>
        
        {/* Floating Icons and Shapes */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-10' : 'opacity-06'}`}>
          <div className="absolute top-[15%] left-[8%] animate-float-slow">
            <Brain className={`w-16 h-16 ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`} />
          </div>
          
          <div className="absolute top-[25%] right-[12%] animate-float-delayed" style={{ animationDelay: '1s' }}>
            <Rocket className={`w-20 h-20 ${isDark ? 'text-[#00FFD1]' : 'text-[#39ADE3]'}`} />
          </div>
          
          <div className="absolute top-[60%] left-[15%] animate-float-slow" style={{ animationDelay: '2s' }}>
            <Target className={`w-14 h-14 ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`} />
          </div>
          
          <div className="absolute bottom-[20%] right-[18%] animate-float-delayed" style={{ animationDelay: '1.5s' }}>
            <Users className={`w-18 h-18 ${isDark ? 'text-[#00FFD1]' : 'text-[#39ADE3]'}`} />
          </div>
          
          <div className="absolute top-[40%] right-[8%] animate-float-slow" style={{ animationDelay: '0.5s' }}>
            <Zap className={`w-12 h-12 ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`} />
          </div>
          
          <div className="absolute bottom-[35%] left-[10%] animate-float-delayed" style={{ animationDelay: '2.5s' }}>
            <Award className={`w-16 h-16 ${isDark ? 'text-[#00FFD1]' : 'text-[#39ADE3]'}`} />
          </div>

          <div className="absolute top-[70%] right-[25%] animate-float-slow" style={{ animationDelay: '3s' }}>
            <Globe className={`w-14 h-14 ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`} />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrollY > 50 ? (isDark ? 'bg-[#0A0B0F]/80' : 'bg-white/80') + ' backdrop-blur-xl shadow-lg' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
              <div className="relative">
                <div className="absolute inset-0 bg-[#39ADE3] blur-xl opacity-50 group-hover:opacity-100 transition-opacity rounded-full"></div>
                <img
                  src="/assets/logo.png"
                  alt="Start & Growth Logo"
                  className="h-8 sm:h-10 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            </Link>

            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className={`rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                data-testid="theme-toggle-btn"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <Button
                variant="ghost"
                onClick={toggleLanguage}
                className={`rounded-full text-sm font-semibold ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                data-testid="language-toggle-btn"
              >
                {language === 'en' ? '🇫🇷' : '🇬🇧'}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-20" data-testid="hero-section">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#0774B6]/10 to-[#39ADE3]/10 border border-[#39ADE3]/20 mb-6">
              <Sparkles className="w-4 h-4 text-[#39ADE3]" />
              <span className={`text-sm font-semibold ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`}>
                {content.badge}
              </span>
            </div>

            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {content.title}
            </h1>

            <p className={`text-base sm:text-lg max-w-3xl mx-auto mb-8 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              {content.subtitle}
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a 
                href={`mailto:hello@startandgrowth.net?subject=${encodeURIComponent(language === 'en' ? 'AI Factory Partnership' : 'Partenariat AI Factory')}`}
                data-testid="contact-cta-btn"
              >
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-[#0774B6] to-[#39ADE3] hover:from-[#0774B6]/90 hover:to-[#39ADE3]/90 text-white rounded-full px-8 py-6 text-base font-bold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  {content.cta1} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {content.stats.map((stat, index) => (
                <div 
                  key={index}
                  className={`p-6 rounded-2xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'} backdrop-blur-sm transition-transform hover:scale-105`}
                  data-testid={`stat-card-${index}`}
                >
                  <div className={`text-3xl font-black mb-2 bg-gradient-to-r from-[#0774B6] to-[#39ADE3] bg-clip-text text-transparent`}>
                    {stat.value}
                  </div>
                  <div className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About Section */}
          <section className="mb-20" data-testid="about-section">
            <div className="text-center mb-12">
              <h2 className={`text-3xl sm:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {content.aboutTitle}
              </h2>
              <p className={`text-base max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                {content.aboutDescription}
              </p>
            </div>
          </section>

          {/* Organized By */}
          <section className="mb-20">
            <div className="text-center mb-8">
              <p className={`text-sm font-bold uppercase tracking-widest ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {language === 'en' ? 'Organized by' : 'Organisé par'}
              </p>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="flex items-center justify-center p-4 rounded-xl transition-transform hover:scale-110">
                <img
                  src="/assets/Logo_8_HUB.jpg"
                  alt="HUB"
                  className="h-16 w-auto object-contain rounded"
                />
              </div>
              <div className="flex items-center justify-center p-4 rounded-xl transition-transform hover:scale-110">
                <img
                  src="/assets/logo.png"
                  alt="Start & Growth"
                  className="h-16 w-auto object-contain"
                />
              </div>
            </div>
          </section>

          {/* Key Features */}
          <section className="mb-20" data-testid="features-section">
            <div className="text-center mb-12">
              <h2 className={`text-3xl sm:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {content.featuresTitle}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {content.features.map((feature, index) => (
                <Card 
                  key={index}
                  className={`p-8 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-200 hover:shadow-xl'} backdrop-blur-sm transition-all duration-300 hover:scale-105 rounded-2xl`}
                  data-testid={`feature-card-${index}`}
                >
                  <div className={`w-14 h-14 rounded-xl ${isDark ? 'bg-gradient-to-br from-[#39ADE3] to-[#0774B6]' : 'bg-gradient-to-br from-[#0774B6] to-[#39ADE3]'} flex items-center justify-center mb-6 shadow-lg`}>
                    {feature.icon === 'users' && <Users className="w-7 h-7 text-white" />}
                    {feature.icon === 'brain' && <Brain className="w-7 h-7 text-white" />}
                    {feature.icon === 'rocket' && <Rocket className="w-7 h-7 text-white" />}
                  </div>
                  <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {feature.title}
                  </h3>
                  <p className={`leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    {feature.description}
                  </p>
                </Card>
              ))}
            </div>
          </section>

          {/* 2026 Roadmap */}
          <section className="mb-20" data-testid="roadmap-section">
            <div className="text-center mb-12">
              <h2 className={`text-3xl sm:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {content.roadmapTitle}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {content.roadmap.map((item, index) => (
                <Card 
                  key={index}
                  className={`p-6 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-200 hover:shadow-lg'} backdrop-blur-sm transition-all duration-300 rounded-2xl`}
                  data-testid={`roadmap-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg ${isDark ? 'bg-gradient-to-br from-[#39ADE3] to-[#0774B6]' : 'bg-gradient-to-br from-[#0774B6] to-[#39ADE3]'} flex items-center justify-center flex-shrink-0`}>
                      {item.icon === 'calendar' && <Calendar className="w-6 h-6 text-white" />}
                      {item.icon === 'video' && <Video className="w-6 h-6 text-white" />}
                      {item.icon === 'book' && <BookOpen className="w-6 h-6 text-white" />}
                      {item.icon === 'globe' && <Globe className="w-6 h-6 text-white" />}
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {item.title}
                      </h3>
                      <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Partner Benefits */}
          <section className="mb-20" data-testid="benefits-section">
            <div className="text-center mb-12">
              <h2 className={`text-3xl sm:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {content.benefitsTitle}
              </h2>
              <p className={`text-base max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                {content.benefitsSubtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
              {/* What Partners Contribute */}
              <Card className={`p-8 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'} backdrop-blur-sm rounded-2xl`}>
                <h3 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {content.contributeTitle}
                </h3>
                <ul className="space-y-4">
                  {content.contribute.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className={`w-6 h-6 flex-shrink-0 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
                      <span className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* What Partners Receive */}
              <Card className={`p-8 ${isDark ? 'bg-gradient-to-br from-[#0774B6]/10 to-[#39ADE3]/10 border-[#39ADE3]/20' : 'bg-gradient-to-br from-[#0774B6]/5 to-[#39ADE3]/5 border-[#39ADE3]/30'} backdrop-blur-sm rounded-2xl`}>
                <h3 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {content.receiveTitle}
                </h3>
                <ul className="space-y-4">
                  {content.receive.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Award className={`w-6 h-6 flex-shrink-0 ${isDark ? 'text-[#00FFD1]' : 'text-[#0774B6]'}`} />
                      <span className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </section>

          {/* Partners Logos */}
          <section className="mb-20" data-testid="partners-section">
            <div className="text-center mb-12">
              <h2 className={`text-3xl sm:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {content.partnersTitle}
              </h2>
            </div>

            <div className={`p-10 rounded-3xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'} backdrop-blur-sm`}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center justify-items-center">
                {/* CCIB */}
                <div className="flex items-center justify-center p-4 rounded-xl bg-white transition-transform hover:scale-110">
                  <img
                    src="/assets/CCIB.png"
                    alt="CCIB"
                    className="h-16 w-auto object-contain"
                  />
                </div>

                {/* GDG Abomey-Calavi */}
                <div className={`flex items-center justify-center p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'} transition-transform hover:scale-110`}>
                  <img
                    src="/assets/GDG.png"
                    alt="GDG Abomey-Calavi"
                    className="h-16 w-auto object-contain"
                  />
                </div>

                {/* 10 000 Codeurs */}
                <div className={`flex items-center justify-center p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'} transition-transform hover:scale-110`}>
                  <img 
                    src={isDark ? "/assets/10000CodeursBlanc6000 px.png" : "/assets/10000CodeursLogo Noir6000 px.png"} 
                    alt="10 000 Codeurs" 
                    className="h-14 w-auto object-contain"
                  />
                </div>

                {/* HUB */}
                <div className={`flex items-center justify-center p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'} transition-transform hover:scale-110`}>
                  <img 
                    src="/assets/Logo_8_HUB.jpg" 
                    alt="HUB" 
                    className="h-16 w-auto object-contain rounded"
                  />
                </div>

                {/* Afropoles Incubator */}
                <div className={`flex items-center justify-center p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'} transition-transform hover:scale-110`}>
                  <img
                    src="/assets/afropoles.jpeg"
                    alt="Afropoles Incubator"
                    className="h-20 w-auto object-contain"
                  />
                </div>

                {/* Indabax Benin */}
                <div className={`flex items-center justify-center p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'} transition-transform hover:scale-110`}>
                  <img 
                    src="/assets/indabax.jpg" 
                    alt="Indabax Benin" 
                    className="h-24 w-auto object-contain"
                  />
                </div>

                {/* NSIA Asset Mgmt */}
                <div className={`flex items-center justify-center p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'} transition-transform hover:scale-110`}>
                  <div className={`text-center font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    <img
                    src="/assets/NSIA AM LOGO.jpeg"
                    alt="NSIA AM"
                    className="h-24 w-auto object-contain"
                    />
                  </div>
                </div>

                {/* DualForce Capital */}
                <div className="flex items-center justify-center p-4 rounded-xl bg-white transition-transform hover:scale-110">
                  <img
                    src="/assets/dualforce.png"
                    alt="DualForce Capital"
                    className="h-16 w-auto object-contain"
                  />
                </div>

                {/* BUBO */}
                <div className={`flex items-center justify-center p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'} transition-transform hover:scale-110`}>
                  <div className={`text-center font-black text-3xl ${isDark ? 'text-[#39ADE3]' : 'text-[#0774B6]'}`}>
                    <img
                    src="/assets/BUBO.png"
                    alt="BUBO"
                    className="h-20 w-auto object-contain"
                    />
                  </div>
                </div>

                {/* DIA Advisory */}
                <div className="flex items-center justify-center p-4 rounded-xl bg-white transition-transform hover:scale-110">
                  <img
                    src="/assets/dia.png"
                    alt="DIA Advisory"
                    className="h-20 w-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center" data-testid="cta-section">
            <Card className={`p-12 ${isDark ? 'bg-gradient-to-br from-[#0774B6]/20 to-[#39ADE3]/20 border-[#39ADE3]/30' : 'bg-gradient-to-br from-[#0774B6]/10 to-[#39ADE3]/10 border-[#39ADE3]/40'} backdrop-blur-sm rounded-3xl`}>
              <h2 className={`text-3xl sm:text-4xl font-black mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {content.ctaTitle}
              </h2>
              <p className={`text-base sm:text-lg max-w-2xl mx-auto mb-8 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                {content.ctaDescription}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="mailto:arosf@startandgrowth.net" data-testid="email-cta-btn">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-[#0774B6] to-[#39ADE3] hover:from-[#0774B6]/90 hover:to-[#39ADE3]/90 text-white rounded-full px-8 py-6 text-base font-bold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  >
                    <Mail className="mr-2 w-5 h-5" />
                    {content.ctaEmail}
                  </Button>
                </a>
                {/* <Link to="/" data-testid="back-home-btn">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className={`rounded-full px-8 py-6 text-base font-bold ${isDark ? 'border-white/20 hover:bg-white/10' : 'border-slate-300 hover:bg-slate-50'} transition-all`}
                  >
                    {content.ctaBackHome}
                  </Button>
                </Link> */}
              </div>
            </Card>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className={`border-t ${isDark ? 'border-white/10' : 'border-slate-200'} py-10 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-20 relative transition-colors duration-500`}>
        <div className="container mx-auto max-w-7xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-8 sm:mb-10 lg:mb-12">
            <div className="space-y-4 sm:space-y-6 sm:col-span-2 lg:col-span-2">
              <img
                src="/assets/logo.png"
                alt="Start & Growth Logo"
                className="h-8 sm:h-10 object-contain"
              />
              <p className={`${isDark ? 'text-white/60' : 'text-slate-600'} leading-relaxed max-w-md text-sm sm:text-base`}>{t.footer.description}</p>
              <div className="flex gap-4">
                <a
                  href="https://www.linkedin.com/company/startandgrowth-ai-consulting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 sm:w-12 sm:h-12 ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'} backdrop-blur-sm hover:bg-gradient-to-r hover:from-[#39ADE3] hover:to-[#00FFD1] border rounded-xl sm:rounded-2xl flex items-center justify-center transition-all duration-300 group hover:scale-110`}
                >
                  <Linkedin className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-white' : 'text-slate-600'} group-hover:text-white transition-colors`} />
                </a>
                <a
                  href="mailto:arosf@startandgrowth.net"
                  className={`w-10 h-10 sm:w-12 sm:h-12 ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'} backdrop-blur-sm hover:bg-gradient-to-r hover:from-[#39ADE3] hover:to-[#00FFD1] border rounded-xl sm:rounded-2xl flex items-center justify-center transition-all duration-300 group hover:scale-110`}
                >
                  <Mail className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-white' : 'text-slate-600'} group-hover:text-white transition-colors`} />
                </a>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <h4 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-4 sm:mb-6 uppercase tracking-wider`}>{t.footer.quickLinks}</h4>
              <nav className="flex flex-col gap-2 sm:gap-3">
                {[t.nav.services, t.nav.portfolio, t.nav.benefits, t.nav.contact].map((item, idx) => (
                  <a key={idx} href={`/#${['services', 'portfolio', 'benefits', 'contact'][idx]}`} className={`${isDark ? 'text-white/60 hover:text-[#00FFD1]' : 'text-slate-600 hover:text-[#0774B6]'} transition-all duration-300 hover:translate-x-2 inline-block text-xs sm:text-sm`}>{item}</a>
                ))}
              </nav>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <h4 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'} mb-4 sm:mb-6 uppercase tracking-wider`}>{t.footer.services}</h4>
              <nav className="flex flex-col gap-2 sm:gap-3">
                {t.footer.servicesItems.map((item, idx) => (
                  <a key={idx} href="/#services" className={`${isDark ? 'text-white/60 hover:text-[#00FFD1]' : 'text-slate-600 hover:text-[#0774B6]'} transition-all duration-300 hover:translate-x-2 inline-block text-xs sm:text-sm`}>{item}</a>
                ))}
              </nav>
            </div>
          </div>
          <div className={`pt-6 sm:pt-8 border-t ${isDark ? 'border-white/10 text-white/50' : 'border-slate-200 text-slate-600'} text-center text-xs sm:text-sm`}>
            <p>{t.footer.copyright.replace('{YEAR}', new Date().getFullYear())}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AIFactory;
